import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import CustomMultiSelect from '../../components/FormElements/MultiSelect';

const BulkCategoryModal = ({
  open,
  onClose,
  categoryTypes,
  onSubmit,
  selectedCount,
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [action, setAction] = useState('add');

  const handleCategoryChange = (selected) => {
    setSelectedCategories(selected);
  };

  const handleActionChange = (event, newAction) => {
    if (newAction !== null) {
      setAction(newAction);
    }
  };

  const handleSubmit = () => {
    if (selectedCategories.length > 0) {
      onSubmit(selectedCategories, action);
    }
  };

  const handleClose = () => {
    setSelectedCategories([]);
    setAction('add');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          width: '450px',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Bulk Category Assignment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedCount} audio(s) selected
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
            Action
          </Typography>
          <ToggleButtonGroup
            value={action}
            exclusive
            onChange={handleActionChange}
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  backgroundColor: action === 'add' ? '#4CAF50' : '#f44336',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: action === 'add' ? '#43A047' : '#E53935',
                  },
                },
              },
            }}
          >
            <ToggleButton value="add">
              <Add sx={{ mr: 1 }} /> Add Categories
            </ToggleButton>
            <ToggleButton value="remove">
              <Remove sx={{ mr: 1 }} /> Remove Categories
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
            Select Categories to {action === 'add' ? 'Add' : 'Remove'}
          </Typography>
          <CustomMultiSelect
            selectOptions={categoryTypes}
            onChange={handleCategoryChange}
            placeholder={`Select categories to ${action}`}
            value={selectedCategories}
          />
        </Box>

        {selectedCategories.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '12px', color: '#666', mb: 1 }}>
              Selected categories:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedCategories.map((catId) => {
                const cat = categoryTypes.find((c) => c._id === catId);
                return cat ? (
                  <Chip
                    key={catId}
                    label={cat.name}
                    size="small"
                    onDelete={() =>
                      setSelectedCategories((prev) =>
                        prev.filter((id) => id !== catId)
                      )
                    }
                    sx={{
                      backgroundColor: action === 'add' ? '#4CAF50' : '#f44336',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': { color: 'white' },
                      },
                    }}
                  />
                ) : null;
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: action === 'add' ? '#e8f5e9' : '#ffebee',
            borderRadius: '8px',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: action === 'add' ? '#2e7d32' : '#c62828' }}>
            {action === 'add'
              ? 'Selected categories will be added to all selected audios. Existing categories will be preserved.'
              : 'Selected categories will be removed from all selected audios. Other categories will be preserved.'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: '#666',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={selectedCategories.length === 0}
          sx={{
            backgroundColor: action === 'add' ? '#4CAF50' : '#f44336',
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: action === 'add' ? '#43A047' : '#E53935',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#999',
            },
          }}
        >
          {action === 'add' ? 'Add' : 'Remove'} Categories
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkCategoryModal;
